<script setup lang="ts">
defineOptions({
  name: 'AppTextField',
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    modelValue: string
    label?: string
    labelTooltip?: string
    id?: string
    name?: string
    type?: string
    autocomplete?: string
    placeholder?: string
    required?: boolean
    disabled?: boolean
    density?: 'default' | 'compact'
    icon?: string
    error?: string | string[] | null
  }>(),
  {
    modelValue: '',
    type: 'text',
    required: false,
    disabled: false,
    density: 'default',
    icon: undefined,
    error: null,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const uid = useId()
const elementId = computed(() => props.id || uid)
const errorId = computed(() => `${elementId.value}-error`)

const errorMessage = computed(() => {
  if (!props.error) return ''
  if (Array.isArray(props.error)) return props.error.find(Boolean) ?? ''
  return props.error
})

const hasError = computed(() => Boolean(errorMessage.value))

const attrs = useAttrs() as Record<string, unknown>
const wrapperClass = computed(() => [
  'app-field',
  props.density === 'compact' ? 'app-field--compact' : undefined,
  attrs.class,
])
const inputAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs
  return rest
})

function onInput(event: Event) {
  emit('update:modelValue', (event.target as HTMLInputElement).value)
}
</script>

<template>
  <div :class="wrapperClass">
    <label v-if="label" class="app-field__label" :for="elementId">
      {{ label }}
      <span
        v-if="labelTooltip"
        class="app-field__label-icon"
        :title="labelTooltip"
        :aria-label="labelTooltip"
      >
        <AppIcon name="hero-information-circle" size="sm" />
      </span>
    </label>

    <div v-if="icon" class="app-input-icon">
      <AppIcon class="app-input-icon__icon" :name="icon" size="md" />
      <input
        v-bind="inputAttrs"
        :id="elementId"
        class="app-input app-input--with-icon"
        :class="{
          'app-input--error': hasError,
          'app-input--compact': density === 'compact',
        }"
        :name="name"
        :type="type"
        :autocomplete="autocomplete"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        :value="modelValue"
        :aria-invalid="hasError ? 'true' : undefined"
        :aria-describedby="hasError ? errorId : undefined"
        @input="onInput"
      >
    </div>

    <input
      v-else
      v-bind="inputAttrs"
      :id="elementId"
      class="app-input"
      :class="{
        'app-input--error': hasError,
        'app-input--compact': density === 'compact',
      }"
      :name="name"
      :type="type"
      :autocomplete="autocomplete"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      :value="modelValue"
      :aria-invalid="hasError ? 'true' : undefined"
      :aria-describedby="hasError ? errorId : undefined"
      @input="onInput"
    >

    <p v-if="hasError" :id="errorId" class="app-field__error">
      {{ errorMessage }}
    </p>
  </div>
</template>
